import React, { useEffect, Fragment, } from 'react';
import { FormGroup, Label, Input, Button, Modal, ModalBody, ModalFooter, Form, } from 'reactstrap';
import { chunk, orderBy, isEmpty, keyBy, omit, mapValues, } from 'lodash';
import { useToggle, useList, } from 'react-use';
import Select from 'react-select';

import useFormState from '../hooks/useFormState';
import { idWithTenant, } from '../../shared/util';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import Field from '../Field';
import { caseFields as fields, caseItemFields, } from '../../shared/models/product';
import ListForm from '../ListForm';
import ProductSelectOptionLabel from '../ProductSelectOptionLabel';

const { keys, entries, } = Object;

export default function CaseFormModal(props) {
  const { itemRef, isOpen, values, chunkProducts, tenant, onClickClose, renderFormBody = _ => _, renderFooterContent = _ => _, isSubmitDisabled = false, } = props;
  const originalItems = values?.items || [];
  const originalItemByProductId = keyBy(originalItems, 'productId');
  const products = chunkProducts;
  const extraSellers = useCollectionSubscription(tenant.ref.collection('extraSellers').orderBy('createdAt'), [tenant.id]);
  const productsById = keyBy(products, 'id');
  const isNew = !values || !values.id;
  const [items, { set: setItems, updateAt: updateItemAt }] = useList(values?.items || [{ productId: null, quantity: 1 }]);
  const [pendingItems, { set: setPendingItem, updateAt: updatePendingItemAt }] = useList(values?.pendingItems || []);
  const statedFields = useFormState(values, omit(fields({ tenant, extraSellers, otherProducts: products.filter(_ => _.id !== values?.id), }), 'productTagIds'), isOpen);
  const isUnsubmittable =
    Object.values(statedFields).some(_ => !_.isValid) ||
    items.length === 0 ||
    items.some(_ => !_.isValid);
  const onSubmit = async (event) => {
    event.preventDefault();
    if(isUnsubmittable) return;

    await props.onSubmit({ ...mapValues(statedFields, 'value'), items: items.map(_ => ({ ...originalItemByProductId[_.productId], ...omit(_, 'isValid') })), pendingItems: pendingItems.map(_ => omit(_, 'isValid')), });
  };

  return (
    <Modal isOpen={isOpen} style={{ minWidth: 800 }}>
      <div className="modal-header">
        <div className="w-100 d-flex justify-content-between">
          <h5 className="modal-title">
            ケース{isNew ? '追加' : '編集'}
          </h5>
        </div>
      </div>
      {
        isSubmitDisabled && (
          <div className="alert alert-warning">
            すでに在庫があるため編集できません
          </div>
        )
      }
      <Form onSubmit={onSubmit}>
        <ModalBody>
          <Fragment>
            <div>
              {
                entries(statedFields).map(([fieldName, fieldSetting]) => (
                  <Field
                    key={fieldName}
                    name={fieldName}
                    documentName="case"
                    {...fieldSetting}
                  />
                ))
              }
            </div>
            <div className="mt-3">
              {
                statedFields.contentType.value != null && (
                  <ListForm
                    initialItem={_ => ({ quantity: 1 })}
                    addButtonLabel="商品追加"
                    items={items}
                    renderItem={(item, itemIndex) => {
                      return (
                        <div className="card p-3">
                          <ItemForm
                            fields={caseItemFields({ items, products, productsById, extraSellerId: statedFields.extraSellerId?.value ?? null, })}
                            index={itemIndex}
                            values={item}
                            product={productsById[item.productId]}
                            products={products}
                            tenant={tenant}
                            contentType={statedFields.contentType.value}
                            extraSellerId={statedFields.extraSellerId?.value ?? null}
                            onChange={_ => updateItemAt(itemIndex, { ...item, ..._, })}
                          />
                        </div>
                      );
                    }}
                    onChange={setItems}
                    minItems={1}
                    renderFooterContent={(base) => {
                      return (
                        <div className="d-flex gap-1 align-items-start">
                          {base}
                        </div>
                      );
                    }}
                  />
                )
              }
            </div>
          </Fragment>
        </ModalBody>
        <ModalFooter>
          {
            renderFooterContent((
              <Fragment>
                <Button className="cancel" color="secondary" onClick={onClickClose}>閉じる</Button>
                <Button className="save" type="submit" color="primary" onClick={onSubmit} disabled={isUnsubmittable, isSubmitDisabled}>保存</Button>
              </Fragment>
            ), { items, onSubmit, isUnsubmittable, })
          }
        </ModalFooter>
      </Form>
    </Modal>
  );
};

function ItemForm (props) {
  const { index: itemIndex, fields, values, tenant, product, onChange, products, contentType, extraSellerId, } = props;
  const statedFields = useFormState(values, fields, values);
  const filteredProducts = products
    .filter(_ => !_.isCase && !_.isSet)
    .filter(_ => (contentType === 'amazon' ? (_.isAmazonProduct && _.deliveryRoute === 'amazon' && _.extraSellerId == extraSellerId) : !_.isAmazonProduct));
  const productOptions = filteredProducts.map((product) => {
    const { id, ref, sku, code, name, asin, } = product;
    return {
      value: id,
      queryLabel: [id, sku, code, asin].join(' '),
      label: (
        <ProductSelectOptionLabel product={product} />
      ),
    };
  });
  useEffect(() => {
    onChange({
      ...mapValues(statedFields, 'value'),
      isValid: Object.values(statedFields).every(_ => _.isValid),
    });
  }, [...Object.values(statedFields).map(_ => _.value), ...Object.values(statedFields).map(_ => _.isValid)]);

  return (
    <div>
      <div className="d-flex flex-wrap">
        {
          entries(statedFields).map(([fieldName, fieldSetting], i) => {
            const width = ({
              productId: 500,
              quantity: 100,
            })[fieldName] || 200;
            return (
              <div className="mr-2" style={{ width, }}>
                <Field
                  key={fieldName}
                  name={fieldName}
                  {...fieldSetting}
                  {...(itemIndex === 0 && i === 0 && { inputProps: { autoFocus: true, } })}
                  {...(fieldName === 'productId' && {
                    options: productOptions,
                    inputProps: {
                      styles: { 
                        valueContainer: _ => ({ ..._, height: 60 }),
                        singleValue: _ => ({ ..._, top: 0, transform: '' }),
                      },
                    },
                  })}
                />
              </div>
            );
          })
        }
      </div>
    </div>
  );
}
